import { FieldArray, FormikProps } from 'formik';
import { useMemo, useState, useEffect, useCallback } from 'react';
import {
  Col,
  Row,
  Utils,
  mapQuery,
  AddButton,
  InputField,
  Responsive,
  FlexLayout,
  SelectField,
  SectionHeader,
} from '@gv/triage-components';

import { Config } from 'config';
import { useAppSelector } from 'store';
import { RemoveFieldButton } from 'components';
import { useListQuery } from 'store/api/states';
import { CommonClientPayload } from 'store/api/clients';
import { selectAuthHospitalId } from 'store/slices/auth';
import { useGetHospitalQuery } from 'store/api/hospital-bitwerx';
import { selectCurrentTask } from 'store/api/action-queue/selectors';

import { PetFields } from '../pet';
import { ageValidate } from '../pet/config';
import { PetFieldsBitwerx } from '../pet/petFieldBitwerx';

import { validationSchema } from './config';
import { PhonesFieldArray } from './phones-field-array';
import { getUniquePhoneError, getRequiredPhoneError } from './helpers';
interface UseClientFormProps {
  isEdit?: boolean;
  hospitalId?: string;
  isEmailRequired?: boolean;
  isTimezoneFieldAvailable?: boolean;
}

export const useClientForm = ({
  hospitalId,
  isEdit = false,
  isEmailRequired = false,
  isTimezoneFieldAvailable = false,
}: UseClientFormProps) => {
  const { data: states, ...props } = useListQuery();
  const task = useAppSelector(selectCurrentTask);
  const authHospitalId = useAppSelector(selectAuthHospitalId);
  const { data } = useGetHospitalQuery({
    id: task?.h_id || hospitalId || authHospitalId,
  });
  const [isPracticeSync, setPracticeSync] = useState<boolean>(false);

  useEffect(() => {
    setPracticeSync(false);

    const getBitwerxStatus = async (response: any) => {
      const bitwerx_practice_id = response?.response?.bitwerx_practice_id;
      if (bitwerx_practice_id) {
        setPracticeSync(true);
      }
    };
    getBitwerxStatus(data);
  }, [data]);
  const isZipCodeRequired = useMemo(
    () =>
      Config.helpers.featureAvailableForHospital(
        hospitalId,
        'REQUIRED_CLIENT_ZIP_CODE'
      ),
    [hospitalId]
  );

  const timezones = useMemo(() => {
    if (!isTimezoneFieldAvailable) {
      return undefined;
    }
    return Utils.Date.getTimezoneOptions();
  }, [isTimezoneFieldAvailable]);

  const isDynamicFlowOrderChanged = process.env.REACT_APP_CHANGE_DYNAMIC_ORDER;
  const schema = useMemo(() => {
    if (isDynamicFlowOrderChanged) {
      const dynamicOrder = JSON.parse(isDynamicFlowOrderChanged);
      const isHospitalInDynamicOrder = dynamicOrder.some(
        (item: any) => item == hospitalId
      );
      return validationSchema(
        isEdit,
        isEmailRequired,
        isHospitalInDynamicOrder ? false : isZipCodeRequired,
        isPracticeSync
      );
    }
    return validationSchema(
      isEdit,
      isEmailRequired,
      isZipCodeRequired,
      isPracticeSync
    );
  }, [
    isEdit,
    isEmailRequired,
    isZipCodeRequired,
    isPracticeSync,
    hospitalId,
    isDynamicFlowOrderChanged,
  ]);

  const validate = useCallback(
    (values: CommonClientPayload) => {
      const errors: Record<string, any> = {};
      const ageErrors = isEdit ? {} : ageValidate(values);

      const { phones, home_phones } = values;

      const somePhoneNumberExist = [...phones, ...home_phones].some(
        (phone) => phone?.length > 0
      );

      if (somePhoneNumberExist) {
        const uniquePhonesErrors = phones.map(
          getUniquePhoneError(phones, home_phones)
        );
        const uniqueHomePhonesErrors = home_phones.map(
          getUniquePhoneError(home_phones, phones)
        );

        if (uniquePhonesErrors.some(Boolean)) {
          errors.phones = uniquePhonesErrors;
        }

        if (uniqueHomePhonesErrors.some(Boolean)) {
          errors.home_phones = uniqueHomePhonesErrors;
        }
      } else {
        errors.phones = phones.map(getRequiredPhoneError);
        errors.home_phones = home_phones.map(getRequiredPhoneError);
      }

      return { ...errors, ...ageErrors };
    },
    [isEdit]
  );

  return {
    validate,
    validationSchema: schema,
    pageDetails: mapQuery(props),
    fields: (formikProps: FormikProps<CommonClientPayload>) => (
      <>
        <Row>
          <Col col={4}>
            <InputField
              name="first_name"
              label="First name"
              placeholder="First name"
            />
          </Col>
          <Col col={4}>
            <InputField
              name="last_name"
              label="Last name"
              placeholder="Last name"
            />
          </Col>
        </Row>
        <SectionHeader title="Contact Info" />
        <Row>
          <Col col={4}>
            <PhonesFieldArray name="phones" label="Mobile phone" />
          </Col>

          <Col col={4}>
            <PhonesFieldArray name="home_phones" label="Home phone" />
          </Col>
        </Row>
        {isDynamicFlowOrderChanged &&
        JSON.parse(isDynamicFlowOrderChanged).some(
          (item: any) => item == hospitalId
        ) ? null : (
          <>
            {timezones && (
              <Row>
                <Col col={4}>
                  <SelectField
                    onlyValue
                    isSearchable
                    name="timezone"
                    label="Timezone"
                    options={timezones}
                  />
                </Col>
              </Row>
            )}
            <FieldArray
              name="emails"
              render={({ push, remove }) => {
                const moreThenOneEmail = formikProps.values.emails.length > 1;

                return (
                  <Row>
                    <Col col={8}>
                      {formikProps.values.emails.map(
                        (_: string, idx: number) => {
                          const isFirstField = idx === 0;

                          return (
                            <Row key={`email_${idx}`}>
                              <Col>
                                <FlexLayout gap={8}>
                                  <InputField
                                    name={`emails.${idx}`}
                                    placeholder={`Email ${idx + 1}`}
                                    label={idx === 0 ? 'Email' : undefined}
                                  />

                                  {moreThenOneEmail && (
                                    <div>
                                      {isFirstField && (
                                        <Responsive.Desktop>
                                          <p>&nbsp;</p>
                                        </Responsive.Desktop>
                                      )}

                                      <RemoveFieldButton
                                        onClick={() => remove(idx)}
                                      />
                                    </div>
                                  )}
                                </FlexLayout>
                              </Col>
                            </Row>
                          );
                        }
                      )}

                      <AddButton
                        asRow
                        text="Add new email"
                        onClick={() => push('')}
                      />
                    </Col>
                  </Row>
                );
              }}
            />
            <SectionHeader title="Address" />
            <Row>
              <Col col={4}>
                <InputField
                  name="address"
                  label="Address 1"
                  placeholder="Address 1"
                />
              </Col>

              <Col col={4}>
                <InputField
                  label="Address 2"
                  name="addresses.0"
                  placeholder="Address 2"
                />
              </Col>
            </Row>
            <Row>
              <Col col={4}>
                <InputField name="city" label="City" placeholder="City" />
              </Col>

              <Col col={2} colTablet={6}>
                <SelectField
                  onlyValue
                  name="state"
                  isSearchable
                  label="State"
                  placeholder="State"
                  options={states?.map((state) => ({
                    value: state.name,
                    label: state.name,
                  }))}
                />
              </Col>

              <Col col={2} colTablet={6}>
                <InputField label="ZIP" name="zip_code" placeholder="ZIP" />
              </Col>
            </Row>
          </>
        )}

        {!isEdit && (
          <>
            <SectionHeader title="Pet" />
            {!isPracticeSync && <PetFields />}
            {isPracticeSync && <PetFieldsBitwerx />}
          </>
        )}
      </>
    ),
  };
};
